import React, { useState } from "react";
import SettingsFieldset from "../settings/SettingsFieldset";
import { _t } from "../../../languageHandler";
import BaseAvatar from "../avatars/BaseAvatar";
import { Room } from "matrix-js-sdk/src/matrix";
import { mediaFromMxc } from "../../../customisations/Media";
import AccessibleButton from "../elements/AccessibleButton";

interface Props {
    room: Room;
    userLevels: Record<string, number>;
    defaultUserLevel: number;
}

/**
 * Mostra la lista di utenti privilegiati (power level > default)
 * con comportamento "Mostra altro / Mostra meno".
 */
const PrivilegedMembersList: React.FC<Props> = ({ room, userLevels, defaultUserLevel }) => {
    const [expanded, setExpanded] = useState(false);

    const privilegedMembers = Object.keys(userLevels)
        .filter((user) => userLevels[user] > defaultUserLevel)
        .map((userId) => room.getMember(userId))
        .filter((m) => !!m) // filtra null o undefined
        .map((member) => {
            let avatarThumbnailUrl: string | undefined | null;

            try {
                const mxcUrl = member.getMxcAvatarUrl?.();
                if (mxcUrl) {
                    const media = mediaFromMxc(mxcUrl);
                    avatarThumbnailUrl = media?.getThumbnailOfSourceHttp(96, 96, "crop");
                }
            } catch (e) {
                console.warn("Errore generando avatar thumbnail per", member.userId, e);
            }

            return {
                ...member,
                displayUserId: member.userId,
                lastModifiedTime: Date.now(),
                isInvite: member.membership === "invite",
                avatarThumbnailUrl,
            };
        });

    const visibleMembers = expanded ? privilegedMembers : privilegedMembers.slice(0, 1);

    return (
        <SettingsFieldset legend={_t("room_settings.permissions.privileged_users_section")}>
            {privilegedMembers.length > 0 ? (
                <>
                    <div className="mx_PrivilegedMembersList">
                        {visibleMembers.map((member) => (
                            <div key={member.userId} className="mx_PrivilegedMembersList_item">
                                <BaseAvatar
                                    size="32px"
                                    name={member.name}
                                    idName={member.userId}
                                    title={member.userId}
                                    url={member.avatarThumbnailUrl}
                                    altText={_t("common.user_avatar")}
                                />
                                <span className="mx_PrivilegedMembersList_name">{member.name}</span>
                            </div>
                        ))}
                    </div>

                    {privilegedMembers.length > 1 && (
                        <div className="mx_InviteDialog_section_showMore">
                            <AccessibleButton onClick={() => setExpanded(!expanded)} kind="link">
                                {expanded ? _t("common.show_less") : _t("common.show_more")}
                            </AccessibleButton>
                        </div>
                    )}

                </>
            ) : (
                <div>{_t("room_settings.permissions.no_privileged_users")}</div>
            )}
        </SettingsFieldset>
    );
};

export default PrivilegedMembersList;
